/**
 * Session genesis — the initial `RuntimeState` for one loaded bundle
 * (architecture.md §3.2, §3.4).
 *
 * @remarks
 * Everything a replay needs is decided HERE, deterministically: flag values
 * from the pack's declarations (record keys inserted in sorted order, per
 * the repo-wide sorted-keys rule, so canonical JSON serialization is
 * trivial), starting inventory from the manifest, the PRNG seeded per the
 * manifest's policy, and — ruled with the R5 plan sign-off — the entry
 * room's arrival narration (description + `firstVisitNarratives`) emitted as
 * turn-0 transcript entries through the same {@link enterRoom} helper every
 * later arrival uses. `(bundle, seed, commands)` reproduces the OPENING
 * beat, not just the turns after it.
 *
 * Seed-policy conflicts are programmer error, not player-facing failure: a
 * caller seed alongside a `fixed` policy, or a missing caller seed under
 * `caller_supplied`, throws {@link EngineInvariantError}.
 *
 * @packageDocumentation
 */

import type { RecordLayer, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { recordForGenesis } from '../record/record-of-run.js';
import { createRandomnessState } from '../rng/random.js';
import { EngineInvariantError } from './engine-invariant-error.js';
import { enterRoom } from './enter-room.js';
import { createSessionStartCursor } from './transcript-cursor.js';

/** Genesis options — the session's two inputs beside the bundle. */
export interface CreateInitialRuntimeStateOptions {
	/** The session seed, when the manifest's policy asks the caller to supply one. */
	readonly callerSeed?: number;
	/**
	 * D241: the Record the host holds for this game — the layers earlier runs
	 * left. Made fit for the pack (`recordForGenesis`) and copied into the
	 * run's own view, which is all `record_holds` ever reads. Absent, the run
	 * begins with none: every replay, every road and every test that hands
	 * nothing in.
	 */
	readonly record?: readonly RecordLayer[];
}

/**
 * Build the complete initial state for one session.
 *
 * @param bundle - The loaded bundle
 * @param options - Caller-supplied genesis inputs (seed, under the right policy)
 * @returns The initial state, entry-room narration already in the transcript
 */
export const createInitialRuntimeState = (
	bundle: EngineGameBundle,
	options: CreateInitialRuntimeStateOptions = {},
): RuntimeState => {
	const { manifest } = bundle.contentPack;

	const stateBeforeEntry: RuntimeState = {
		mapDiscovery: { revealedRoomIds: []},
		narrative: {
			// D235: no actor has asked anything yet.
			askedPromptIds: [],
			firedTriggerIds: [],
			occurrenceTallies: {},
			readLogIds: [],
			selectedEndingIds: [],
			// D231: no `once` topic has been said.
			spentTopicIds: [],
			voiceLineLastSpokenTurn: {},
			voiceLineUsage: {},
		},
		progression: {
			activeRoomId: manifest.entryRoomId,
			discoveredSecretRoomIds: [],
			turnCount: 0,
			visitedRoomIds: [],
		},
		randomness: createRandomnessState(resolveSessionSeed(bundle, options)),
		// D241: the run's own view of the Record — what it was handed, and nothing written yet.
		record: { heldAtGenesis: recordForGenesis(bundle, options.record), writtenThisRun: []},
		scheduler: { firedEventIds: [], pendingEvents: [], processedEventIds: [], sequenceCounter: 0 },
		session: { commandHistory: [], recentEntityRefs: [], responseUsage: {}, transcript: []},
		world: {
			// R25: no takes yet, nothing set down yet — both records start empty.
			acquisitions: [],
			// D234: every foe is whole and nobody has been struck — both records
			// are deltas, and the player's health is the manifest's until a foe
			// draws blood (no `playerHealth` key: the delta is nothing).
			actorHealth: {},
			// D232: nobody has moved yet — every actor stands where the pack put it.
			actorLocations: {},
			attackedActorIds: [],
			// D244: nothing has moved a counter yet — every one is at its declared `initial`.
			counterValues: {},
			droppedItemLocations: {},
			// D143: nothing is worn at genesis — equipping is an act.
			equippedItemIds: {},
			flagValues: buildInitialFlagValues(bundle),
			inventoryItemIds: manifest.initialInventory,
			// D50: door state, seeded from the pack exactly as `flagValues` is —
			// declarations set the starting position, the runtime record carries it
			// from there, and "is it open?" never has to also know what it started
			// as. The player has searched nothing yet.
			openedContainerItemIds: itemIdsDeclaring(bundle, (item) => item.openable?.state === 'open'),
			removedRoomItemIds: [],
			// D138: no hidden exit has been revealed at genesis — the ledger fills
			// as the reveal watcher writes it, never from declarations.
			revealedExitIds: [],
			// D240: nor gone through anybody.
			searchedActorIds: [],
			searchedContainerItemIds: [],
			spentUses: {},
			// D56: switch state, seeded exactly as door state is — same shape, same
			// reason, and now the same seeder.
			switchedOnItemIds: itemIdsDeclaring(bundle, (item) => item.switchable?.state === 'on'),
			unlockedExitIds: [],
		},
	};

	// The opening emission: same helper, same one-shot mechanics as every
	// later arrival — the entry room is simply the first unvisited room.
	const entry = enterRoom(bundle, stateBeforeEntry, manifest.entryRoomId, createSessionStartCursor());

	return {
		...entry.state,
		session: { ...entry.state.session, transcript: entry.entries },
	};
};

/**
 * The ids of every item whose declaration puts it in a binary state at genesis.
 *
 * @remarks
 * Declarations set the starting position and the runtime record carries it from
 * there, exactly as `flagValues` has since R5. A toggle-relative-to-authored
 * scheme was the alternative and it cannot answer "is it open?" without also
 * knowing what it started as — the derived-data trap §3.2 forbids.
 *
 * One seeder for both sets: a door and a switch differ only in which property
 * they read, which is the same observation that let their handlers and their
 * effects collapse into one each (D56).
 *
 * @param bundle - The loaded bundle
 * @param isSetAtGenesis - Whether this item declares the state as its starting position
 * @returns The ids that start in that state
 */
const itemIdsDeclaring = (
	bundle: EngineGameBundle,
	isSetAtGenesis: (item: EngineGameBundle['contentPack']['items'][number]) => boolean,
): readonly RuntimeState['world']['openedContainerItemIds'][number][] =>
	bundle.contentPack.items.filter((item) => isSetAtGenesis(item)).map((item) => item.itemId);

/** Flag record from declarations, keys inserted in sorted order (canonical-JSON-friendly). */
const buildInitialFlagValues = (bundle: EngineGameBundle): RuntimeState['world']['flagValues'] => {
	const sortedFlags = [...bundle.contentPack.flags].sort((first, second) =>
		first.flagId < second.flagId ? -1 : 1,
	);

	return Object.fromEntries(sortedFlags.map((flag) => [flag.flagId, flag.initialValue]));
};

/**
 * The genesis state for a seed the caller would quote — used under
 * `caller_supplied`, ignored under `fixed`, where the pack bakes its own
 * seed into every session and refuses a caller's. One place for that rule,
 * because every host and the engine's own `restart` (D179) all need it.
 *
 * @param bundle - The loaded bundle
 * @param seed - The caller's seed
 * @param record - D241: the Record the run begins on — none when absent
 * @returns The genesis state
 */
export const genesisStateOf = (bundle: EngineGameBundle, seed: number, record?: readonly RecordLayer[]): RuntimeState =>
	createInitialRuntimeState(bundle, {
		...(bundle.contentPack.manifest.seedPolicy.type === 'fixed' ? {} : { callerSeed: seed }),
		...(record === undefined ? {} : { record }),
	});

/** Resolve the session seed per the manifest's policy; conflicts throw (programmer error). */
const resolveSessionSeed = (bundle: EngineGameBundle, options: CreateInitialRuntimeStateOptions): number => {
	const { seedPolicy } = bundle.contentPack.manifest;

	switch (seedPolicy.type) {
		case 'caller_supplied': {
			if (options.callerSeed === undefined) {
				throw new EngineInvariantError(
					'This bundle\'s seed policy is caller_supplied, but no callerSeed was provided',
				);
			}

			return options.callerSeed;
		}
		case 'fixed': {
			if (options.callerSeed !== undefined) {
				throw new EngineInvariantError(
					'This bundle\'s seed policy is fixed; providing a callerSeed contradicts the manifest',
				);
			}

			return seedPolicy.seed;
		}
	}
};
